import React, { createContext, useState, useCallback, useEffect, useContext } from 'react';
import alertAudioFile from '../../assets/alert.mp3';
import { appAgentService } from '../services/central-api/app-agent';

const sound = new Audio(alertAudioFile);

interface OccurrenceContextData {
    occurrences: Occurrence[];
    isLoading: boolean;
}

const OccurrenceContext = createContext<OccurrenceContextData>({} as OccurrenceContextData);

function hasSameItems(a: number[], b: number[]) {
    if (a.length !== b.length) {
        return false;
    }
    const aSorted = [...a].sort();
    const bSorted = [...b].sort();
    return aSorted.every((val, i) => val === bSorted[i]);
}

export const OccurrenceProvider: React.FC<{ children: React.ReactNode; }> = ({ children }) => {
    const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fetchOccurrences = useCallback(async (isInitialLoad = false) => {
        try {
            if (isInitialLoad) {
                setIsLoading(true);
            }

            const occurrencesRes = await appAgentService.getOccurrences();
            const newIds = occurrencesRes.map(o => o.id || 0);

            setOccurrences(currentOccurrences => {
                const currentIds = currentOccurrences.map(o => o.id || 0);

                if (!hasSameItems(currentIds, newIds)) {
                    sound.currentTime = 0;
                    sound.play().catch(err => console.error(err));

                    return occurrencesRes;
                }

                return currentOccurrences;
            });
        } catch (err) {
            console.error(err);
        } finally {
            if (isInitialLoad) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        fetchOccurrences(true);

        const interval = setInterval(() => {
            fetchOccurrences(false);
        }, 5000);

        return () => clearInterval(interval);
    }, [fetchOccurrences]);

    return (
        <OccurrenceContext.Provider value={{ occurrences, isLoading }}>
            {children}
        </OccurrenceContext.Provider>
    );
};

export function useOccurrences() {
    return useContext(OccurrenceContext);
}
